import { WaterbodyDetail } from '@omniafishing/core';
import { noop } from 'lodash';
import { Maximize, Wind } from 'lucide-react';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useFrontload } from 'react-frontload';
import { MapRef } from 'react-map-gl';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { ContentWrapper } from '../../components/content_wrapper/content_wrapper';
import { PRO_SIGNUP_LOGIN_MODAL_OPEN_PARAMS } from '../../components/login_signup_modal/signup_login_modal';
import { LoadableMap } from '../../components/map/loadable_omnia_map';
import { BoatLandingModal } from '../../components/map/map_modals/boat_landing_modal/boat_landing_modal';
import {
  BASE_LAYER,
  LAYER_TYPES,
  MapboxBoatLanding,
  MapLayerState,
  POINTS_OF_INTEREST_LAYER,
  WEATHER_LAYER,
} from '../../components/map/map_types';
import { InteractiveLayerIds } from '../../components/map/omnia_map_sources';
import { OmniaButton } from '../../components/omnia_button/omnia_button';
import { OmniaLinkButton } from '../../components/omnia_button/omnia_link_button';
import { ProTrialButton } from '../../components/pro_buttons/pro_trial_button';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { SEO } from '../../components/seo/seo';
import { WaterbodyDetailExploreNearby } from '../../components/waterbody_detail/waterbody_detail_sections/waterbody_detail_explore_nearby/waterbody_detail_explore_nearby';
import { WaterbodyDetailHeader } from '../../components/waterbody_detail/waterbody_detail_sections/waterbody_detail_header/waterbody_detail_header';
import { useDimensions } from '../../hooks/use_dimensions';
import { useProUpsellModal } from '../../hooks/use_pro_upsell_modal';
import { useUser } from '../../hooks/use_user';
import { apiV1 } from '../../lib/api';
import { fitBounds } from '../../lib/fit_bounds';
import { OmniaUrls } from '../../lib/urls';
import { SignupLoginModalActions } from '../../redux/signup_login_modal';

interface WaterbodyBoatRampsPageProps {
  waterbodyDetail: WaterbodyDetail;
}

export const WaterbodyBoatRampsPage = (props: WaterbodyBoatRampsPageProps) => {
  const { waterbodyDetail } = props;

  const { primary_name, water_temperature_recorded_at, url_slug, bbox } = waterbodyDetail;
  const { openProUpsellModal } = useProUpsellModal();
  const { isPro } = useUser();
  const dispatch = useDispatch();
  const [mapViewport, setMapViewport] = useState<{
    longitude: number;
    latitude: number;
    zoom: number;
  }>();
  const [mapRef, setMapRef] = useState<MapRef>();
  const [windActive, setwindActive] = useState(false);
  const mapLayerState: MapLayerState = {
    [LAYER_TYPES.BASE]: BASE_LAYER.SATELLITE,
    [LAYER_TYPES.OVERLAY]: [],
    [LAYER_TYPES.CORE]: undefined,
    [LAYER_TYPES.WEATHER]: windActive ? [WEATHER_LAYER.WIND] : [],
    [LAYER_TYPES.POINTS_OF_INTEREST]: [POINTS_OF_INTEREST_LAYER.BOAT_LANDINGS],
  };
  const [selectedBoatLanding, setSelectedBoatLanding] = useState<MapboxBoatLanding>();
  const isBoatLandingModalOpen = !!selectedBoatLanding?.AccessName;

  const { data, frontloadMeta } = useFrontload(`WaterbodyBoatRampsPage:${url_slug}`, async () => {
    try {
      const res = await apiV1.waterbodyBoatLandingsFetch(url_slug);
      return res.data.data;
    } catch (e) {
      console.log(e);
    }
  });

  const boatLandings = data || [];

  const [measureRef, measureBounds] = useDimensions({
    onResize: true,
    onElementResize: true,
  });

  useLayoutEffect(() => {
    if (measureBounds.width != null) {
      const bboxMidPoint = {
        lng: (bbox[0][0] + bbox[2][0]) / 2,
        lat: (bbox[0][1] + bbox[2][1]) / 2,
      };

      const z =
        measureBounds.width && bbox
          ? fitBounds(
              [bbox[0], bbox[2]],
              {
                width: measureBounds.width,
                height: measureBounds.height,
              },
              {
                top: 20,
                left: 20,
                bottom: 20,
                right: 20,
              }
            ).zoom
          : 13;

      setMapViewport({
        longitude: bboxMidPoint.lng,
        latitude: bboxMidPoint.lat,
        zoom: z,
      });
    }
  }, [measureBounds]);

  const handleMapClick = useCallback((e: mapboxgl.MapLayerMouseEvent) => {
    const { features } = e;

    const boatLandingFeature = features.find(
      (feature) => feature.layer.id === InteractiveLayerIds.BOAT_LANDINGS
    );

    if (boatLandingFeature) {
      setSelectedBoatLanding(boatLandingFeature.properties as MapboxBoatLanding);
    }
  }, []);

  useEffect(() => {
    if (mapRef) {
      const map = mapRef.getMap();
      updateIconSize(selectedBoatLanding?.AccessName, map);
    }
  }, [selectedBoatLanding]);

  const updateIconSize = useCallback((name: string, map: mapboxgl.Map) => {
    if (name) {
      map.setLayoutProperty(InteractiveLayerIds.BOAT_LANDINGS, 'icon-size', [
        'match',
        ['get', 'AccessName'],
        name,
        0.5,
        0.25,
      ]);
    } else {
      map.setLayoutProperty(InteractiveLayerIds.BOAT_LANDINGS, 'icon-size', 0.25);
    }
  }, []);

  if (!frontloadMeta.done) {
    return null;
  }

  return (
    <ScrollToTop>
      <SEO
        title={`Boat Ramps & Launches on ${primary_name} – Access Map, Wind & Tips`}
        description={`Interactive map of every public and private boat launch on ${primary_name}. See ramp facilities, wind direction, weather, fishing reports and more.`}
      />
      <section className="pb-9 bg-[#e9eeec]">
        <WaterbodyDetailHeader
          headingTag="p"
          productToShowInFollowModal={null}
          scrollToFishingReport={() => null}
          waterbody={waterbodyDetail}
          lastUpdatedDate={water_temperature_recorded_at}
          showPublishDates
          navElement={null}
          showBreadcrumbs={false}
          showButtons={false}
        />
        <ContentWrapper className="py-6">
          <section className="bg-white rounded-lg p-3">
            <div className="relative">
              <h1 className="text-2xl mb-2">Top Boat Ramps for Fishing {primary_name}</h1>
              <p className="mb-9">
                Use this interactive guide to explore every boat landing on {primary_name}. See
                access type, live wind conditions, and plan your launch near top fishing spots.
              </p>

              <div className="mb-3">
                <div
                  className="h-[400px] relative select-none rounded-xl overflow-hidden"
                  ref={measureRef}
                >
                  {measureBounds.width != null && mapViewport != null && (
                    <>
                      <LoadableMap
                        height={measureBounds.height}
                        loadInteractiveImages
                        mapLayerState={mapLayerState}
                        markers={[]}
                        navControl={false}
                        onMapClick={handleMapClick}
                        onLoad={(_bounds, _mapRef) => {
                          setMapRef(_mapRef);
                        }}
                        onViewportChange={noop}
                        viewport={mapViewport}
                        waypoints={[]}
                        width={measureBounds.width}
                        disableScrollZoom
                      />
                      <BoatLandingModal
                        boatLanding={selectedBoatLanding}
                        onClose={() => setSelectedBoatLanding(null)}
                        isOpen={isBoatLandingModalOpen}
                      />
                      <div className="absolute left-3 top-3 z-10 flex gap-3">
                        <OmniaLinkButton
                          shape="square"
                          kind="primary"
                          size="lg"
                          to={OmniaUrls.map({
                            lat: waterbodyDetail.lat,
                            lng: waterbodyDetail.lng,
                            waterbodySlug: url_slug,
                          })}
                        >
                          <Maximize />
                        </OmniaLinkButton>
                        <OmniaButton
                          shape="square"
                          kind="tertiary"
                          size="lg"
                          onClick={() => {
                            setwindActive(!windActive);
                          }}
                        >
                          <Wind />
                        </OmniaButton>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex justify-center items-center">
                  <Link
                    to={OmniaUrls.map({
                      lat: waterbodyDetail.lat,
                      lng: waterbodyDetail.lng,
                      waterbodySlug: url_slug,
                    })}
                    className="flex gap-1 p-3 items-center text-black hover:text-black"
                  >
                    <Maximize /> Full Map
                  </Link>
                  <div
                    className="flex gap-1 p-3 items-center cursor-pointer"
                    onClick={() => {
                      setwindActive(!windActive);
                    }}
                  >
                    <Wind /> Wind Layer
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border rounded-lg mb-6 max-w-[600px] ml-auto mr-auto text-center">
              <h2 className="text-lg mb-1">{primary_name} Interactive Fishing Map</h2>
              <p className="mb-2">
                Explore our detailed {primary_name} map with real-time fishing conditions, hot
                spots, and local reports. View {primary_name}-specific species data, seasonal
                patterns, and discover the most effective fishing techniques from local experts—all
                on our interactive map.
              </p>
              <OmniaLinkButton
                className="ml-auto mr-auto flex max-w-fit"
                kind="primary"
                size="lg"
                to={OmniaUrls.map({
                  lat: waterbodyDetail.lat,
                  lng: waterbodyDetail.lng,
                  waterbodySlug: url_slug,
                })}
              >
                View Full {primary_name} Map
              </OmniaLinkButton>
            </div>

            <h2 className="text-lg mb-3 sm:text-center">
              Full List of Boat Ramps (Public and Private Access)
            </h2>
            <div className="mb-6 flex justify-center">
              <table className="border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border text-left p-3">Ramp Name</th>
                    <th className="border text-left p-3">Access Type</th>
                    <th className="border text-left p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {boatLandings.map((b) => {
                    const mapPageMapLayerState: Partial<MapLayerState> = {
                      [LAYER_TYPES.POINTS_OF_INTEREST]: [POINTS_OF_INTEREST_LAYER.BOAT_LANDINGS],
                    };
                    const mapUrl = OmniaUrls.map({ lat: b.lat, lng: b.lng });
                    const mapUrlPathname = mapUrl.split('?')[0];
                    const mapUrlSearch = mapUrl.split('?')[1];
                    const mapBoatLanding: MapboxBoatLanding = {
                      AccessName: b.name,
                      County: b.county,
                      HUC12: '',
                      HUC12_Name: '',
                      Latitude: b.lat,
                      Longitude: b.lng,
                      State: b.state,
                      Type: b.access,
                    };
                    return (
                      <tr key={b.id} className="odd:bg-white even:bg-gray-100">
                        <td className="border p-3">{b.name}</td>
                        <td className="border p-3">{b.access}</td>
                        <td className="border p-3">
                          <Link
                            to={{
                              pathname: mapUrlPathname,
                              search: mapUrlSearch,
                              state: {
                                mapLayerState: mapPageMapLayerState,
                                selectedBoatLanding: mapBoatLanding,
                              },
                            }}
                            className="underline text-[#006B99]"
                          >
                            View On Map
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-3 items-center p-4 bg-[#e9eeec] rounded-lg max-w-[400px] ml-auto mr-auto">
              <h2 className="text-center text-lg mb-1">
                Smarter Boat Launches for {primary_name} with Omnia PRO
              </h2>

              <OmniaButton kind="tertiary" size="lg" onClick={openProUpsellModal}>
                Learn More About Omnia Pro
              </OmniaButton>
              <div className="flex gap-3">
                <ProTrialButton size={'lg'} kind="tertiary" text={'7-day Trial'} />
                <OmniaButton
                  onPress={() => {
                    dispatch(
                      SignupLoginModalActions.SIGNUP_LOGIN_MODAL_OPEN(
                        PRO_SIGNUP_LOGIN_MODAL_OPEN_PARAMS
                      )
                    );
                  }}
                  kind="primary"
                  size={'lg'}
                  isDisabled={isPro}
                >
                  {isPro ? 'Already PRO' : 'Buy PRO'}
                </OmniaButton>
              </div>
            </div>

            <div>
              <WaterbodyDetailExploreNearby
                slug={url_slug}
                waterbody={waterbodyDetail}
                linkToBoatRampsPage
              />
            </div>
          </section>
        </ContentWrapper>
      </section>
    </ScrollToTop>
  );
};
